import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_INTERVAL_MS = 30_000;
const BASELINE_STORAGE_KEY = 'support-ticket-baseline';

/**
 * useSupportTicketPulse
 *
 * Central hook for the admin Support experience:
 *   - Polls /api/support/tickets/stats/summary every 30s
 *   - Returns the active count (open + in_progress) for the red badge
 *   - Fires a browser Notification when the active count grows vs the last-seen baseline
 *   - Persists baseline in localStorage so notifications don't re-fire on refresh
 *
 * Returns:
 *   { count, permission, requestPermission, acknowledgeSeen }
 */
export function useSupportTicketPulse({ enabled = true } = {}) {
  const [count, setCount] = useState(0);
  const [permission, setPermission] = useState(
    typeof Notification !== 'undefined' ? Notification.permission : 'unsupported'
  );
  const baselineRef = useRef(null);

  // Load baseline from localStorage on mount
  useEffect(() => {
    const stored = Number(localStorage.getItem(BASELINE_STORAGE_KEY) || 0);
    baselineRef.current = Number.isFinite(stored) ? stored : 0;
  }, []);

  const fireNotification = useCallback((newCount, delta) => {
    if (typeof Notification === 'undefined') return;
    if (Notification.permission !== 'granted') return;
    try {
      const title = delta === 1 ? 'New support ticket' : `${delta} new support tickets`;
      const body =
        newCount === 1
          ? 'You have 1 open ticket in the AADC admin inbox.'
          : `You have ${newCount} open tickets in the AADC admin inbox.`;
      const notification = new Notification(title, {
        body,
        icon: '/favicon-192.png',
        tag: 'support-ticket-pulse',
        renotify: true,
      });
      notification.onclick = () => {
        window.focus();
        notification.close();
      };
    } catch (e) {
      // Silent — some browsers throw on the constructor when tab is hidden
    }
  }, []);

  const poll = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/support/tickets/stats/summary`, {
        withCredentials: true,
      });
      const active = (res.data.open || 0) + (res.data.in_progress || 0);
      setCount(active);

      const baseline = baselineRef.current ?? 0;
      if (baseline > 0 && active > baseline) {
        fireNotification(active, active - baseline);
      }
      // Baseline advances forward (never backward) so we don't spam on refresh
      if (active > baseline) {
        baselineRef.current = active;
        localStorage.setItem(BASELINE_STORAGE_KEY, String(active));
      }
    } catch (e) {
      // Silent — no permission / not logged in / network hiccup
    }
  }, [fireNotification]);

  useEffect(() => {
    if (!enabled) return undefined;
    poll();
    const id = setInterval(poll, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [enabled, poll]);

  const requestPermission = useCallback(async () => {
    if (typeof Notification === 'undefined') return 'unsupported';
    if (Notification.permission === 'granted') return 'granted';
    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      return result;
    } catch (e) {
      return 'denied';
    }
  }, []);

  const acknowledgeSeen = useCallback(() => {
    // Called when the admin opens the inbox — resets baseline to current count
    // so the next new ticket triggers a notification again.
    baselineRef.current = count;
    localStorage.setItem(BASELINE_STORAGE_KEY, String(count));
  }, [count]);

  return { count, permission, requestPermission, acknowledgeSeen };
}
